// src/components/ProjectList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchProjects } from '../services/api';

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const navigate = useNavigate();

  // プロジェクト一覧を取得
  const loadProjects = useCallback(async (pageNum = 0) => {
    try {
      setLoading(true);
      const response = await fetchProjects(pageNum);
      
      if (pageNum === 0) {
        // 最初のページの場合は置き換え
        setProjects(response.data);
      } else {
        // 2ページ目以降は追加
        setProjects(prev => [...prev, ...response.data]);
      }
      
      setHasNextPage(response.pagination.hasNextPage);
      setPage(response.pagination.currentPage);
      setError(null);
    } catch (err) {
      setError('プロジェクトの読み込みに失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初回ロード
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // スクロール検知による追加読み込み
  const handleScroll = useCallback((e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      if (hasNextPage && !loading) {
        loadProjects(page + 1);
      }
    }
  }, [hasNextPage, loading, loadProjects, page]);

  // プロジェクト詳細へ遷移
  const handleProjectClick = (id) => {
    navigate(`/${id}`);
  };

  // 日付フォーマット
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP');
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">ヒアリングプロジェクト一覧</h1>
        <Link to="/create">
          <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
            新規作成
          </button>
        </Link>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div 
        className="overflow-auto h-[calc(100vh-160px)]" 
        onScroll={handleScroll}
      >
        <table className="min-w-full bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">相談内容（要約）</th>
              <th className="px-4 py-2 text-left">担当者</th>
              <th className="px-4 py-2 text-left">クライアント名</th>
              <th className="px-4 py-2 text-left">予算</th>
              <th className="px-4 py-2 text-left">スケジュール</th>
              <th className="px-4 py-2 text-left">相談日時</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr 
                key={project.id} 
                onClick={() => handleProjectClick(project.id)}
                className="border-b hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-4 py-3">{project.summary || '(未ヒアリング)'}</td>
                <td className="px-4 py-3">{project.assignee}</td>
                <td className="px-4 py-3">{project.client_name}</td>
                <td className="px-4 py-3">{project.budget || '-'}</td>
                <td className="px-4 py-3">{project.schedule || '-'}</td>
                <td className="px-4 py-3">{formatDate(project.created_at)}</td>
              </tr>
            ))}
            {projects.length === 0 && !loading && (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  プロジェクトがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {loading && (
          <div className="text-center p-4">
            <div className="inline-block animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <p className="mt-2">読み込み中...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectList;